// 유입 경로 분석 유틸리티

export type ReferrerSource = 
  | 'Direct'
  | 'Google'
  | 'Naver'
  | 'Daum'
  | 'Bing'
  | 'Yahoo'
  | 'Facebook'
  | 'Instagram'
  | 'Twitter'
  | 'LinkedIn'
  | 'GitHub'
  | 'Reddit'
  | 'YouTube'
  | 'Pinterest'
  | 'TikTok'
  | 'Kakao'
  | 'Line'
  | 'Other';

interface ReferrerInfo {
  source: ReferrerSource;
  url: string | null;
}

// 검색엔진 도메인 매핑
const searchEngines: Record<string, ReferrerSource> = {
  'google.com': 'Google',
  'google.co.kr': 'Google',
  'naver.com': 'Naver',
  'daum.net': 'Daum',
  'bing.com': 'Bing',
  'yahoo.com': 'Yahoo',
};

// SNS 도메인 매핑
const socialMedia: Record<string, ReferrerSource> = {
  'facebook.com': 'Facebook',
  'fb.com': 'Facebook',
  'instagram.com': 'Instagram',
  'twitter.com': 'Twitter',
  'x.com': 'Twitter',
  't.co': 'Twitter',
  'linkedin.com': 'LinkedIn',
  'github.com': 'GitHub',
  'reddit.com': 'Reddit',
  'youtube.com': 'YouTube',
  'youtu.be': 'YouTube',
  'pinterest.com': 'Pinterest',
  'tiktok.com': 'TikTok',
  'kakao.com': 'Kakao',
  'line.me': 'Line',
};

/**
 * Referrer URL을 분석하여 유입 소스 판별
 */
export function analyzeReferrer(referrerUrl: string): ReferrerInfo {
  if (!referrerUrl || referrerUrl.trim() === '') {
    return { source: 'Direct', url: null };
  }

  try {
    const url = new URL(referrerUrl);
    const hostname = url.hostname.toLowerCase().replace('www.', '');

    // 검색엔진 체크
    for (const [domain, source] of Object.entries(searchEngines)) {
      if (hostname.includes(domain)) {
        return { source, url: referrerUrl };
      }
    }

    // SNS 체크
    for (const [domain, source] of Object.entries(socialMedia)) {
      if (hostname.includes(domain)) {
        return { source, url: referrerUrl };
      }
    }

    // 기타 외부 사이트
    return { source: 'Other', url: referrerUrl };
  } catch (error) {
    return { source: 'Direct', url: null };
  }
}

/**
 * URL 파라미터에서 UTM 정보 추출
 */
export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
}

export function extractUTMParams(url: string): UTMParams {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_content: params.get('utm_content') || undefined,
      utm_term: params.get('utm_term') || undefined,
      ref: params.get('ref') || undefined,
    };
  } catch (error) {
    return {};
  }
}

/**
 * User Agent 분석
 */
export interface DeviceInfo {
  deviceType: 'Mobile' | 'Tablet' | 'Desktop';
  browser: string;
  os: string;
}

export function analyzeUserAgent(userAgent: string): DeviceInfo {
  const ua = userAgent.toLowerCase();

  // 디바이스 타입 판별
  let deviceType: DeviceInfo['deviceType'] = 'Desktop';
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
    deviceType = 'Tablet';
  } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
    deviceType = 'Mobile';
  }

  // 브라우저 판별
  let browser = 'Unknown';
  if (ua.includes('edg/')) browser = 'Edge';
  else if (ua.includes('chrome/')) browser = 'Chrome';
  else if (ua.includes('safari/') && !ua.includes('chrome')) browser = 'Safari';
  else if (ua.includes('firefox/')) browser = 'Firefox';
  else if (ua.includes('opera/') || ua.includes('opr/')) browser = 'Opera';
  else if (ua.includes('trident/')) browser = 'IE';

  // OS 판별
  let os = 'Unknown';
  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac os')) os = 'macOS';
  else if (ua.includes('linux')) os = 'Linux';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';

  return { deviceType, browser, os };
}

/**
 * 고유 방문자 ID 생성 또는 가져오기
 */
export function getOrCreateVisitorId(): string {
  const STORAGE_KEY = 'visitor_id';
  
  let visitorId = localStorage.getItem(STORAGE_KEY);
  
  if (!visitorId) {
    // UUID v4 생성
    visitorId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    
    localStorage.setItem(STORAGE_KEY, visitorId);
  }
  
  return visitorId;
}

/**
 * 세션 내 중복 방문 체크
 */
export function isNewSession(): boolean {
  const SESSION_KEY = 'session_tracked';
  const SESSION_DURATION = 30 * 60 * 1000; // 30분
  
  const lastTracked = sessionStorage.getItem(SESSION_KEY);
  
  if (!lastTracked) {
    sessionStorage.setItem(SESSION_KEY, Date.now().toString());
    return true;
  }
  
  const timeDiff = Date.now() - parseInt(lastTracked, 10);
  
  if (timeDiff > SESSION_DURATION) {
    sessionStorage.setItem(SESSION_KEY, Date.now().toString());
    return true;
  }
  
  return false;
}
